/**
 * API Service Base Configuration
 * AIDLC Administrative Backend
 *
 * Axios instance with MSAL token interceptor (production)
 * or plain requests (dev bypass mode).
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

if (!DEV_AUTH_BYPASS) {
  /**
   * Production: attach MSAL access token to every API request.
   * Uses the shared singleton from msalInstance.ts (same instance as main.tsx).
   */
  apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        const { ensureMsalInitialized } = await import('../msalInstance');
        const { apiRequest } = await import('../authConfig');
        const msalInstance = await ensureMsalInitialized();
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });
          if (tokenResponse.accessToken) {
            config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
          }
        }
      } catch (error) {
        console.error('[API] Failed to acquire token silently:', error);
      }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );
}

/**
 * Response interceptor: handle authentication errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      console.error('[API] 401 Unauthorized -- token may be expired');
    }
    if (error.response?.status === 403) {
      console.error('[API] 403 Forbidden -- insufficient permissions');
    }
    return Promise.reject(error);
  }
);

export default apiClient;
